/**
 * Runtime column manager for the data grid (ADR-0010 G2 hide / G3 pin / G4
 * reorder). A "Columns" menu lets the portal user, per data_list:
 *  - show / hide columns (checkbox)        — when `canHide`
 *  - pin / unpin a column to the left      — when `canPin`
 *  - drag columns into a new order         — when `canReorder`
 *
 * The caller (`DataList`) owns the merged preference state and its per-user
 * localStorage persistence; this component is presentational + emits intents.
 *
 * Like `ColumnFilter`, the popover is `position: fixed` (computed from the
 * trigger) so it escapes the table's `overflow-x-auto` clip while staying in
 * the themed DOM subtree (a `document.body` portal would lose `.tray-light`).
 */
import { ArrowLeftToLine, ArrowRightToLine, Check, Columns3, GripVertical, Pin, Plus, X } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";

import { type PinSide, type SavedView, moveItem } from "@/lib/dataGrid";

const POPOVER_W = 256;

export function ColumnManager({
  columns,
  hidden,
  pinned,
  canHide,
  canPin,
  canReorder,
  onToggleHidden,
  onCyclePin,
  onReorder,
  onReset,
  views,
  onSaveView,
  onApplyView,
  onDeleteView,
}: {
  /** All columns in their current display order. */
  columns: string[];
  hidden: Set<string>;
  /** Column → frozen edge (absent = not pinned). */
  pinned: Record<string, PinSide>;
  canHide: boolean;
  canPin: boolean;
  canReorder: boolean;
  onToggleHidden: (column: string) => void;
  /** Cycle a column's pin: none → left → right → none. */
  onCyclePin: (column: string) => void;
  /** The full new column order after a drag. */
  onReorder: (columns: string[]) => void;
  /** Restore the admin defaults (clear user overrides). */
  onReset: () => void;
  /** Saved views (G10) — runtime-only; absent in the builder preview. When
   *  `onSaveView` is provided the popover grows a "Views" section. */
  views?: SavedView[];
  onSaveView?: (name: string) => void;
  onApplyView?: (view: SavedView) => void;
  onDeleteView?: (name: string) => void;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; width: number; maxH: number } | null>(null);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [viewName, setViewName] = useState("");
  const wrapRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const hiddenCount = canHide ? columns.filter((c) => hidden.has(c)).length : 0;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc, true);
    return () => document.removeEventListener("mousedown", onDoc, true);
  }, [open]);

  const openPopover = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r && typeof window !== "undefined") {
      const width = Math.min(POPOVER_W, window.innerWidth - 16);
      const left = Math.max(8, Math.min(r.right - width, window.innerWidth - width - 8));
      const top = r.bottom + 4;
      setPos({ top, left, width, maxH: window.innerHeight - top - 8 });
    }
    setOpen(true);
  };

  const drop = (to: number) => {
    if (dragIdx != null && dragIdx !== to) onReorder(moveItem(columns, dragIdx, to));
    setDragIdx(null);
  };

  return (
    <div ref={wrapRef} className="relative inline-block self-end">
      <button
        ref={btnRef}
        type="button"
        aria-label="Manage columns"
        onClick={() => (open ? setOpen(false) : openPopover())}
        className="inline-flex items-center gap-1.5 rounded-[var(--portal-radius)] border border-border bg-surface px-2.5 py-1.5 text-xs font-medium text-muted-foreground shadow-sm transition hover:bg-accent/5 hover:text-foreground"
      >
        <Columns3 className="h-3.5 w-3.5" />
        Columns{hiddenCount > 0 ? ` (${columns.length - hiddenCount}/${columns.length})` : ""}
      </button>
      {open && pos && (
        <div
          role="dialog"
          style={{ position: "fixed", top: pos.top, left: pos.left, width: pos.width, maxHeight: pos.maxH }}
          className="portal-pop z-50 flex flex-col overflow-hidden rounded-[var(--portal-radius)] border border-border bg-elevated p-2 text-foreground shadow-xl"
        >
          {onSaveView && (
            <div className="mb-2 border-b border-border pb-2">
              <p className="mb-1 px-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                Views
              </p>
              {(views ?? []).map((v) => (
                <div key={v.name} className="flex items-center gap-1 rounded px-1 py-0.5 text-xs hover:bg-accent/10">
                  <button
                    type="button"
                    onClick={() => onApplyView?.(v)}
                    className="flex-1 truncate text-left"
                    title={`Apply "${v.name}"`}
                  >
                    {v.name}
                  </button>
                  <button
                    type="button"
                    aria-label={`Delete view ${v.name}`}
                    onClick={() => onDeleteView?.(v.name)}
                    className="shrink-0 rounded p-0.5 text-muted-foreground/40 transition hover:text-danger"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              <form
                className="mt-1 flex items-center gap-1"
                onSubmit={(e) => {
                  e.preventDefault();
                  const n = viewName.trim();
                  if (n) {
                    onSaveView(n);
                    setViewName("");
                  }
                }}
              >
                <input
                  value={viewName}
                  onChange={(e) => setViewName(e.target.value)}
                  placeholder="Save current view…"
                  className="w-full rounded border border-border bg-background px-1.5 py-1 text-xs outline-none focus:border-accent"
                />
                <button
                  type="submit"
                  aria-label="Save view"
                  disabled={!viewName.trim()}
                  className="shrink-0 rounded border border-border p-1 text-muted-foreground transition hover:bg-accent/10 hover:text-foreground disabled:opacity-40"
                >
                  <Plus className="h-3 w-3" />
                </button>
              </form>
            </div>
          )}
          <div className="min-h-0 flex-1 overflow-auto">
            {columns.map((c, i) => {
              const visible = !hidden.has(c);
              const side = pinned[c];
              const PinIcon = side === "left" ? ArrowLeftToLine : side === "right" ? ArrowRightToLine : Pin;
              const pinLabel = side === "left" ? `Pin ${c} right` : side === "right" ? `Unpin ${c}` : `Pin ${c} left`;
              return (
                <div
                  key={c}
                  draggable={canReorder}
                  onDragStart={canReorder ? () => setDragIdx(i) : undefined}
                  onDragOver={canReorder ? (e) => e.preventDefault() : undefined}
                  onDrop={canReorder ? () => drop(i) : undefined}
                  className={`flex items-center gap-1.5 rounded px-1 py-1 text-xs hover:bg-accent/10 ${
                    dragIdx === i ? "opacity-50" : ""
                  } ${canReorder ? "cursor-grab active:cursor-grabbing" : ""}`}
                >
                  {canReorder && <GripVertical className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50" />}
                  {canHide ? (
                    <button
                      type="button"
                      onClick={() => onToggleHidden(c)}
                      className="flex flex-1 items-center gap-2 text-left"
                    >
                      <span
                        className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border ${
                          visible ? "border-accent bg-accent text-accent-foreground" : "border-border"
                        }`}
                      >
                        {visible && <Check className="h-2.5 w-2.5" />}
                      </span>
                      <span className="truncate">{c}</span>
                    </button>
                  ) : (
                    <span className="flex-1 truncate">{c}</span>
                  )}
                  {canPin && (
                    <button
                      type="button"
                      aria-label={pinLabel}
                      title={pinLabel}
                      aria-pressed={!!side}
                      onClick={() => onCyclePin(c)}
                      className={`shrink-0 rounded p-0.5 transition ${
                        side ? "text-accent" : "text-muted-foreground/40 hover:text-foreground"
                      }`}
                    >
                      <PinIcon className="h-3 w-3" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-2 flex items-center justify-end border-t border-border pt-2">
            <button type="button" onClick={onReset} className="text-xs text-muted-foreground hover:text-foreground">
              Reset
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
